import { ReactElement, HTMLProps, SVGProps } from "react";
import { useTheme } from "styled-components";

import { brandColors } from "_/style";

const COLORS = {
  light: {
    chevronTop: brandColors.blue,
    chevronBottom: brandColors.black,
    textAon: brandColors.black,
    text3d: brandColors.blue,
  },
  dark: {
    chevronTop: brandColors.blue,
    chevronBottom: brandColors.blue,
    textAon: brandColors.white,
    text3d: brandColors.blue,
  },
  lightMono: {
    chevronTop: brandColors.black,
    chevronBottom: brandColors.black,
    textAon: brandColors.black,
    text3d: brandColors.black,
  },
  darkMono: {
    chevronTop: brandColors.white,
    chevronBottom: brandColors.white,
    textAon: brandColors.white,
    text3d: brandColors.white,
  },
};

type LogoProps = {
  /** Specify the theme variant used. */
  darkMode?: boolean;

  /** Display the logo grayscale monochromatic. */
  monochromatic?: boolean;
} & HTMLProps<HTMLDivElement>;

export const Logo = ({
  darkMode = true,
  monochromatic = false,
  ...props
}: LogoProps): ReactElement => {
  const currentTheme = useTheme();

  let logoTheme: typeof currentTheme.name | `${typeof currentTheme.name}Mono` =
    currentTheme.name;
  if (darkMode) {
    logoTheme = "dark";
  }

  if (monochromatic) {
    logoTheme = `${logoTheme}Mono`;
  }

  const colors = COLORS[logoTheme];

  return (
    <div {...props}>
      <svg
        viewBox="0 0 1492 250"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M418.734 150.744H485.422L452.543 56.7615L418.734 150.744ZM519.231 246.588L498.139 186.724H406.017L384.925 246.588H336.538L426.179 3.1015H477.047L569.789 246.588H519.231Z"
          fill={colors.textAon}
        />
        <path
          d="M687.655 42.8041C642.37 42.8041 607.941 80.6453 607.941 125C607.941 169.045 642.99 206.576 687.655 206.576C732.32 206.576 767.37 169.045 767.37 125C767.68 80.9555 732.94 42.8041 687.655 42.8041ZM687.655 250C618.797 250 562.035 194.789 562.035 125.62C562.035 55.8314 618.176 0.00012207 687.655 0.00012207C757.134 0.00012207 813.275 55.8314 813.275 125.62C813.275 194.789 756.514 250 687.655 250Z"
          fill={colors.textAon}
        />
        <path
          d="M997.828 3.1015H1044.35V246.588H997.828L885.545 78.1635V246.588H839.329V3.1015H885.545L997.828 171.526V3.1015Z"
          fill={colors.textAon}
        />
        <path
          d="M1206.58 119.417C1219.29 124.69 1228.91 132.134 1236.04 142.37C1243.18 152.606 1246.59 164.702 1246.59 178.35C1246.59 199.752 1238.52 217.122 1222.08 230.149C1205.65 243.176 1184.24 249.69 1157.57 249.69C1131.2 249.69 1110.11 242.556 1094.6 228.598C1079.09 214.64 1071.65 196.03 1071.96 172.767H1117.56C1117.25 185.484 1120.97 195.099 1128.41 201.923C1135.86 208.747 1145.78 212.469 1158.19 212.469C1170.6 212.469 1180.83 209.057 1188.59 202.544C1196.34 196.03 1200.37 187.345 1200.37 176.489C1200.37 165.013 1196.34 156.328 1187.97 150.124C1179.59 143.921 1168.73 140.819 1155.09 140.819H1134.62V102.357H1151.36C1165.63 102.357 1176.49 99.2557 1183.93 93.3624C1191.38 87.4691 1195.1 79.4046 1195.1 69.1689C1195.1 59.5535 1191.69 51.7991 1184.55 45.9058C1177.42 40.0125 1168.11 37.221 1156.64 37.221C1143.61 37.221 1133.69 40.943 1127.17 48.077C1120.66 55.211 1117.25 64.8264 1117.87 76.613H1071.96C1071.03 53.6602 1078.47 35.3599 1094.29 21.0919C1110.11 7.13411 1131.2 0.00012207 1157.57 0.00012207C1182.07 0.00012207 1201.92 5.89342 1217.74 17.9902C1233.56 30.087 1241.32 45.9058 1241.32 66.0671C1241.32 77.5435 1238.21 88.0894 1232.32 97.3947C1226.12 106.7 1217.74 114.144 1206.58 119.417Z"
          fill={colors.text3d}
        />
        <path
          d="M1317.93 207.196H1361.66C1388.03 207.196 1407.88 199.752 1421.84 184.863C1435.79 169.975 1442.62 149.814 1442.62 124.379C1442.62 97.7045 1435.48 77.5432 1420.91 63.2752C1406.33 49.3174 1386.79 42.1834 1361.97 42.1834H1317.93V207.196ZM1271.71 246.588V3.1015H1365.69C1403.85 3.1015 1434.55 14.2678 1457.2 36.2901C1479.84 58.3124 1491.32 88.0891 1491.32 125C1491.32 162.221 1479.84 191.687 1457.2 213.709C1434.55 235.732 1403.85 246.588 1365.69 246.588H1271.71Z"
          fill={colors.text3d}
        />
        <path
          d="M0 6.10352e-05V68.8586L272.332 168.735L294.665 107.94L0 6.10352e-05Z"
          fill={colors.chevronTop}
        />
        <path
          d="M61.1042 176.799V250L228.598 185.484L131.203 149.814L61.1042 176.799Z"
          fill={colors.chevronBottom}
        />
      </svg>
    </div>
  );
};

export const LogoFullSize = (props: SVGProps<SVGSVGElement>): ReactElement => {
  const currentTheme = useTheme();
  const {
    name: theme,
    color: {
      brand: { lightGrey, darkGrey },
    },
  } = currentTheme;

  return (
    <svg
      width={(34 / 68) * 434}
      height={34}
      viewBox="0 0 434 68"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M71.8806 33.7719H83.328L77.6843 17.7046L71.8806 33.7719ZM89.1317 50.1573L85.5111 39.923H69.6975L66.0769 50.1573H57.7707L73.1586 8.53084H81.8905L97.8108 50.1573H89.1317Z"
        fill={theme === "dark" ? lightGrey : darkGrey}
      />
      <path
        d="M118.042 15.3178C110.269 15.3178 104.359 21.7871 104.359 29.3701C104.359 36.9 110.375 43.3162 118.042 43.3162C125.71 43.3162 131.726 36.9 131.726 29.3701C131.78 21.8402 125.816 15.3178 118.042 15.3178ZM118.042 50.7401C106.222 50.7401 96.4785 41.3012 96.4785 29.4761C96.4785 17.545 106.116 8 118.042 8C129.969 8 139.606 17.545 139.606 29.4761C139.606 41.3012 129.863 50.7401 118.042 50.7401Z"
        fill={theme === "dark" ? lightGrey : darkGrey}
      />
      <path
        d="M171.29 8.53084H179.277V50.1573H171.29L152.016 21.3635V50.1573H144.082V8.53084H152.016L171.29 37.3247V8.53084Z"
        fill={theme === "dark" ? lightGrey : darkGrey}
      />
      <path
        d="M207.122 28.4156C209.305 29.317 210.956 30.5896 212.18 32.3396C213.405 34.0895 213.991 36.1575 213.991 38.4907C213.991 42.1496 212.606 45.1192 209.784 47.3464C206.962 49.5735 203.288 50.687 198.709 50.687C194.184 50.687 190.563 49.4674 187.901 47.0812C185.238 44.6949 183.961 41.5133 184.014 37.5362H191.841C191.788 39.7104 192.427 41.3543 193.705 42.5209C194.983 43.6875 196.686 44.3237 198.816 44.3237C200.946 44.3237 202.703 43.7404 204.034 42.6269C205.365 41.5133 206.057 40.0285 206.057 38.1726C206.057 36.2106 205.365 34.7258 203.927 33.6653C202.49 32.6047 200.626 32.0744 198.284 32.0744H194.769V25.4991H197.645C200.094 25.4991 201.958 24.9688 203.236 23.9613C204.513 22.9538 205.152 21.575 205.152 19.8251C205.152 18.1812 204.566 16.8556 203.342 15.8481C202.117 14.8406 200.52 14.3633 198.55 14.3633C196.313 14.3633 194.61 14.9996 193.491 16.2192C192.373 17.4389 191.788 19.0827 191.894 21.0977H184.014C183.854 17.1737 185.132 14.0451 187.848 11.6059C190.563 9.21963 194.184 8 198.709 8C202.916 8 206.323 9.00752 209.039 11.0756C211.755 13.1436 213.085 15.8481 213.085 19.2949C213.085 21.2569 212.553 23.0598 211.541 24.6506C210.477 26.2415 209.039 27.5141 207.122 28.4156Z"
        fill="#1950FF"
      />
      <path
        d="M226.236 43.4228H233.744C238.269 43.4228 241.677 42.1502 244.073 39.6049C246.469 37.0596 247.64 33.6128 247.64 29.2645C247.64 24.7042 246.416 21.2574 243.914 18.8181C241.411 16.4319 238.057 15.2123 233.797 15.2123H226.236V43.4228ZM218.302 50.1573V8.53084H234.436C240.985 8.53084 246.256 10.4398 250.143 14.2048C254.03 17.9697 256 23.0603 256 29.3706C256 35.7339 254.03 40.7715 250.143 44.5364C246.256 48.3014 240.985 50.1573 234.436 50.1573H218.302Z"
        fill="#1950FF"
      />
      <path
        d="M0 8V19.7721L46.7489 36.8469L50.5825 26.4536L0 8Z"
        fill="#1950FF"
      />
      <path
        d="M10.4906 38.2254V50.7399L39.2428 39.7102L22.5239 33.6121L10.4906 38.2254Z"
        fill="#1950FF"
      />
      <path
        d="M306.456 28.944C310.544 30.456 312.672 34.04 312.672 38.576C312.672 41.768 311.608 44.456 309.424 46.696C307.24 48.88 304.104 50 299.96 50H282.32V9.4H299.232C302.984 9.4 306.008 10.408 308.248 12.424C310.488 14.44 311.608 17.128 311.608 20.432C311.608 24.24 309.76 27.32 306.456 28.944ZM298.336 14.44H288.2V26.816H298.336C302.984 26.816 305.616 24.464 305.616 20.656C305.616 16.68 302.816 14.44 298.336 14.44ZM288.2 44.904H298.56C303.768 44.904 306.568 42.496 306.568 38.408C306.568 33.928 303.488 31.688 298.56 31.688H288.2V44.904ZM342.049 45.912C342.329 45.912 342.609 45.856 343.001 45.8V50C342.049 50.28 341.097 50.392 340.089 50.392C336.785 50.392 335.049 48.712 334.881 45.352C333.537 48.544 329.897 50.56 325.473 50.56C319.817 50.56 316.121 47.2 316.121 41.88C316.121 36.504 319.929 33.368 325.529 32.92L331.745 32.304C333.089 32.192 334.545 31.968 334.545 29.56C334.545 26.592 332.417 25.08 329.001 25.08C325.137 25.08 322.561 26.984 322.113 30.064H316.569C317.409 23.96 322.169 20.488 329.169 20.488C336.729 20.488 340.313 24.352 340.313 30.68V43.504C340.313 45.128 340.873 45.912 342.049 45.912ZM334.713 37.624V36.168L327.097 36.784C323.569 37.12 321.833 38.688 321.833 41.544C321.833 44.288 323.793 45.912 326.873 45.912C329.169 45.912 331.073 45.128 332.529 43.56C333.985 41.992 334.713 40.032 334.713 37.624ZM345.393 41.432H351.049C351.105 44.4 353.401 46.136 357.041 46.136C360.457 46.136 362.585 44.568 362.585 42.104C362.585 40.816 362.137 39.864 361.073 39.36C360.121 38.744 359.897 38.688 358.833 38.408L353.849 37.008C348.641 35.608 346.009 32.976 346.009 29C346.009 23.904 350.265 20.488 356.593 20.488C363.033 20.488 367.401 23.904 367.401 29.224H361.801C361.745 26.536 359.673 24.968 356.593 24.968C353.457 24.968 351.665 26.424 351.665 28.72C351.665 30.904 353.065 31.576 356.201 32.416L361.409 33.76C366.001 34.992 368.297 37.624 368.297 41.712C368.297 47.088 363.761 50.56 356.985 50.56C350.097 50.56 345.561 46.976 345.393 41.432ZM380.789 14.552H373.733V8.896H380.789V14.552ZM380.117 50H374.461V21.048H380.117V50ZM385.947 41.432H391.603C391.659 44.4 393.955 46.136 397.595 46.136C401.011 46.136 403.139 44.568 403.139 42.104C403.139 40.816 402.691 39.864 401.627 39.36C400.675 38.744 400.451 38.688 399.387 38.408L394.403 37.008C389.195 35.608 386.563 32.976 386.563 29C386.563 23.904 390.819 20.488 397.147 20.488C403.587 20.488 407.955 23.904 407.955 29.224H402.355C402.299 26.536 400.227 24.968 397.147 24.968C394.011 24.968 392.219 26.424 392.219 28.72C392.219 30.904 393.619 31.576 396.755 32.416L401.963 33.76C406.555 34.992 408.851 37.624 408.851 41.712C408.851 47.088 404.315 50.56 397.539 50.56C390.651 50.56 386.115 46.976 385.947 41.432Z"
        fill="#B3B3B3"
      />
      <path
        d="M411.484 9.806H407.988V7.45H417.678V9.806H414.182V20.104H411.484V9.806ZM422.504 20.104H419.844V7.45H423.074L426.266 16.988L429.458 7.45H432.612V20.104H429.99V17.254C429.99 15.43 430.066 13.72 430.218 12.124L427.558 20.104H424.936L422.276 12.124C422.428 13.264 422.504 14.974 422.504 17.254V20.104Z"
        fill="#B3B3B3"
      />
    </svg>
  );
};

export const LogoSquare = (props: HTMLProps<HTMLDivElement>): ReactElement => {
  return (
    <div {...props}>
      <svg
        width="54"
        height="54"
        viewBox="0 0 54 54"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="54" height="54" fill="#1950FF" />
        <rect x="7" y="7" width="40" height="40" rx="2" fill="#1950FF" />
        <path
          d="M15.75 17.3125V22.6171L36.7296 30.3112L38.45 25.6279L15.75 17.3125Z"
          fill="#0B0D0F"
        />
        <path
          d="M20.4573 30.9325V36.5717L33.3604 31.6016L25.8575 28.8537L20.4573 30.9325Z"
          fill="#0B0D0F"
        />
      </svg>
    </div>
  );
};
